import { SupportedLanguages } from '@/locales';
import { User, UserBrand, UserRestaurant, UserRoles } from '@/services/innovorder/user/user.type';

export type LoginPayload = {
    email: string;
    password: string;
    locale: SupportedLanguages;
    rememberMe?: boolean;
    captchaToken?: string;
};

export type AuthUser = {
    accessFlags: Record<AccessFlags, boolean>;
    userId: number;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    password: string;
    passwordSalt: string;
    passwordService: null;
    role: UserRoles;
    forgottenPasswordToken: string | null;
    forgottenPasswordTokenDate: string | null;
    additionalAccessFlags: string | null;
    disallowedAccessFlags: string | null;
    lastConnection: string;
    brandId: number;
    created_at: string;
    updated_at: string;
    brands: UserBrand[];
    restaurants: UserRestaurant[];
    brandGroupId: number;
};

export enum AccessFlags {
    CAN_ACCESS_BRAND_HISTORY = 'CAN_ACCESS_BRAND_HISTORY',
    CAN_ACCESS_CUSTOMERS_SECTION = 'CAN_ACCESS_CUSTOMERS_SECTION',
    CAN_ACCESS_GRANTS_AND_ENTRANCE_FEES_SECTION = 'CAN_ACCESS_GRANTS_AND_ENTRANCE_FEES_SECTION',
    CAN_ACCESS_IO_PAY = 'CAN_ACCESS_IO_PAY',
    CAN_ARCHIVE_CUSTOMERS = 'CAN_ARCHIVE_CUSTOMERS',
    CAN_CREATE_ORDERS_WITH_NEGATIVE_AMOUNT = 'CAN_CREATE_ORDERS_WITH_NEGATIVE_AMOUNT',
    CAN_CREATE_ORDERS_WITH_NEGATIVE_BALANCE = 'CAN_CREATE_ORDERS_WITH_NEGATIVE_BALANCE',
    CAN_CREDIT_POST_PAYMENT_CUSTOMERS_EWALLET = 'CAN_CREDIT_POST_PAYMENT_CUSTOMERS_EWALLET',
    CAN_EDIT_AREAS = 'CAN_EDIT_AREAS',
    CAN_EDIT_AUTO_BILLING_REFERENTIAL = 'CAN_EDIT_AUTO_BILLING_REFERENTIAL',
    CAN_EDIT_CUSTOMERS_CREDENTIALS = 'CAN_EDIT_CUSTOMERS_CREDENTIALS',
    CAN_EXPORT_CUSTOMERS_CSV = 'CAN_EXPORT_CUSTOMERS_CSV',
    CAN_FORCE_DEFAULT_CREDENTIALS_CHANGE = 'CAN_FORCE_DEFAULT_CREDENTIALS_CHANGE',
    CAN_MANAGE_BO_USERS = 'CAN_MANAGE_BO_USERS',
    CAN_MANAGE_BRAND_EWALLET_OVERDRAFT = 'CAN_MANAGE_BRAND_EWALLET_OVERDRAFT',
    CAN_MANAGE_CUSTOMER_EWALLET_OVERDRAFT = 'CAN_MANAGE_CUSTOMER_EWALLET_OVERDRAFT',
    CAN_ONLY_EDIT_PRICE_AND_PRODUCT_NAME = 'CAN_ONLY_EDIT_PRICE_AND_PRODUCT_NAME',
    CAN_SYNC_SALES = 'CAN_SYNC_SALES',
    CAN_CHANGE_OWN_PASSWORD = 'CAN_CHANGE_OWN_PASSWORD',
    CAN_EDIT_TABLE_LAYOUT = 'CAN_EDIT_TABLE_LAYOUT',
    CAN_CREATE_ORDER_WITH_CUSTOM_PRICE = 'CAN_CREATE_ORDER_WITH_CUSTOM_PRICE',
    CAN_CREATE_ORDER_WITH_CUSTOM_PRICE_FOR_ALL_PRODUCTS = 'CAN_CREATE_ORDER_WITH_CUSTOM_PRICE_FOR_ALL_PRODUCTS',
    MENU_CATEGORY_CREATE_OR_DELETE = 'MENU_CATEGORY_CREATE_OR_DELETE',
    MENU_CATEGORY_EDIT = 'MENU_CATEGORY_EDIT',
    MENU_CREATE_OR_DELETE = 'MENU_CREATE_OR_DELETE',
    MENU_EDIT = 'MENU_EDIT',
    MENU_PRODUCT_CREATE_OR_DELETE = 'MENU_PRODUCT_CREATE_OR_DELETE',
    MENU_PRODUCT_EDIT = 'MENU_PRODUCT_EDIT',
    MENU_PRODUCT_EDIT_PRICE_ONLY = 'MENU_PRODUCT_EDIT_PRICE_ONLY',
    MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY = 'MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY',
    MENU_STEP_CREATE_OR_DELETE = 'MENU_STEP_CREATE_OR_DELETE',
    MENU_STEP_EDIT = 'MENU_STEP_EDIT',
    MENU_TAG_CREATE_OR_DELETE = 'MENU_TAG_CREATE_OR_DELETE',
    MENU_TAG_EDIT = 'MENU_TAG_EDIT',
    CAN_CANCEL_ORDER_WITH_CONTRA_ENTRY = 'CAN_CANCEL_ORDER_WITH_CONTRA_ENTRY',
    CAN_CREATE_SKU = 'CAN_CREATE_SKU',
}

export type AuthResponseData = {
    user: AuthUser;
    tokenType: string;
    accessToken: string;
};

export type MfaMethod = 'totp' | 'webauthn';

export type MfaRequiredData = {
    mfaToken: string;
    methods?: MfaMethod[];
};

export type LoginResponseData = AuthResponseData | MfaRequiredData;

export const isMfaRequiredData = (data: LoginResponseData): data is MfaRequiredData =>
    typeof (data as MfaRequiredData).mfaToken === 'string';

export type LoginResponse = {
    access_token: string;
    status: number;
    code: string;
    message: string;
    data: LoginResponseData;
    token_type: string;
};

export type MfaSetupTotpResponse = { qrCodeUri: string };
export type MfaVerifyResponse = { accessToken: string; redirectUri?: string };
export type MfaConfirmTotpResponse = { accessToken: string; backupCodes: string[]; redirectUri?: string };

// WebAuthn registration ceremony — the API echoes the spec-shape `options` JSON
// produced by @simplewebauthn/server. We import the spec type so the browser SDK
// can consume the payload without runtime validation.
export type MfaSetupWebauthnOptionsResponse = {
    options: import('@simplewebauthn/browser').PublicKeyCredentialCreationOptionsJSON;
};

export type MfaConfirmWebauthnResponse = {
    accessToken: string;
    backupCodes?: string[];
    redirectUri?: string;
};

export type WebauthnAttestation = import('@simplewebauthn/browser').RegistrationResponseJSON;

// WebAuthn authentication ceremony (verify flow at login).
export type MfaVerifyWebauthnOptionsResponse = {
    options: import('@simplewebauthn/browser').PublicKeyCredentialRequestOptionsJSON;
};

export type WebauthnAssertion = import('@simplewebauthn/browser').AuthenticationResponseJSON;

// Settings page — listing the user's enrolled MFA factors.
export type TotpMethod = {
    userMfaConfigId: number;
    method: 'totp';
    createdAt: string;
};

export type WebauthnCredentialListItem = {
    userPasskeyId: number;
    deviceName: string | null;
    lastUsedAt: string | null;
    createdAt: string;
    aaguid: string | null;
    backupEligible: boolean;
};

export type MfaMethodsListResponse = {
    totp: TotpMethod[];
    webauthn: WebauthnCredentialListItem[];
};

export type AllowedIpsResponse = {
    allowedIps: string[];
};

export type WebauthnCredentialCreateResponse = {
    userPasskeyId: number;
    deviceName: string | null;
    createdAt: string;
    backupCodes?: string[];
};

export type TotpSetupOptionsResponse = {
    qrCodeUri: string;
    setupId: string;
};

export type TotpCredentialCreateResponse = {
    userMfaConfigId: number;
    method: 'totp';
    createdAt: string;
    backupCodes?: string[];
};

export type LoginAsGuestPayload = { userId: number };
export type LoginAsGuestResponse = { accessToken: string; user: User };

export type BrandGuestLoginPayload = {
    brandId: number;
};

export type ForgotPasswordPayload = {
    email: string;
    'g-recaptcha-response': string;
};

export type ResetPasswordPayload = {
    token: string;
    userId: number;
    newPassword: string;
};

export type VerifyForgotPasswordTokenPayload = {
    token: string;
    userId: number;
};
